// src/hooks/useReminders.js
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://uk.backend.hms.hiradentallab.com';

const checkIsAdmin = () => {
  const userRole = String(localStorage.getItem('userRole') || '').trim().toLowerCase();
  let roleFromData = '';
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    roleFromData = String(userData.role || '').trim().toLowerCase();
  } catch (e) {
    roleFromData = '';
  }
  return userRole === 'admin' || roleFromData === 'admin';
};

export const useReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminReady, setAdminReady] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);

  // ⭐ Track if modal was shown this SESSION (persists across reloads using sessionStorage)
  const sessionModalShownKey = 'reminderModalShownThisSession';

  useEffect(() => {
    const check = () => {
      const adminStatus = checkIsAdmin();
      setIsAdmin(adminStatus);
      setAdminReady(true);
      return adminStatus;
    };

    check();
    const timer = setTimeout(() => {
      check();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getHeaders = () => ({
    'x-user-id': localStorage.getItem('userId') || '',
    'x-user-name': localStorage.getItem('userName') || '',
    'x-user-role': (localStorage.getItem('userRole') || '').toLowerCase()
  });

  // ⭐ Check if modal was already shown this session
  const wasModalShownThisSession = useCallback(() => {
    return sessionStorage.getItem(sessionModalShownKey) === 'true';
  }, []);

  // ⭐ Mark modal as shown for this session
  const markModalShownThisSession = useCallback(() => {
    sessionStorage.setItem(sessionModalShownKey, 'true');
  }, []);

  // ⭐ Fetch reminder count for badge
  const fetchReminderCount = useCallback(async () => {
    if (!checkIsAdmin()) return 0;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/reminders/count`,
        { headers: getHeaders() }
      );
      const count = res.data.count || 0;
      setReminderCount(count);
      return count;
    } catch (err) {
      return 0;
    }
  }, []);

  // ⭐ Check on login - Only shows modal ONCE per session
  const checkOnLogin = useCallback(async () => {
    if (!checkIsAdmin()) {
      return { hasReminders: false, count: 0, reminders: [] };
    }

    // ⭐ If modal was already shown this session, don't show again
    if (wasModalShownThisSession()) {
      // But still fetch count for badge
      await fetchReminderCount();
      return { hasReminders: false, count: 0, reminders: [], alreadyShown: true };
    }

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/reminders/check-on-login`,
        {},
        { headers: getHeaders() }
      );
      const list = res.data.reminders || [];
      setReminders(list);
      setReminderCount(res.data.count || 0);

      // ⭐ Mark that modal was shown this session
      if (res.data.hasReminders && res.data.count > 0) {
        markModalShownThisSession();
      }

      return {
        hasReminders: res.data.hasReminders,
        count: res.data.count,
        reminders: list
      };
    } catch (err) {
      return { hasReminders: false, count: 0, reminders: [] };
    }
  }, [fetchReminderCount, wasModalShownThisSession, markModalShownThisSession]);

  // ⭐ Fetch all pending reminders (for AllRemindersPage)
  const fetchAllReminders = useCallback(async () => {
    if (!checkIsAdmin()) return [];
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/reminders/all`,
        { headers: getHeaders() }
      );
      const list = res.data.reminders || [];
      setReminders(list);
      setReminderCount(list.filter(r => r.isOverdue || r.isDueToday).length);
      return list;
    } catch (err) {
      return [];
    }
  }, []);

  // ⭐ REMOVED: pollForNewReminders - No more polling since it's date-based

  const removeReminder = useCallback((reminderId) => {
    if (reminderId === 'all') {
      setReminders([]);
      setReminderCount(0);
    } else {
      setReminders(prev => {
        const updated = prev.filter(r => r._id !== reminderId);
        setReminderCount(updated.filter(r => r.isOverdue || r.isDueToday).length);
        return updated;
      });
    }
  }, []);

  const resetSession = useCallback(() => {
    sessionStorage.removeItem(sessionModalShownKey);
    setReminders([]);
    setReminderCount(0);
  }, [sessionModalShownKey]);

  return {
    reminders,
    loading,
    error,
    isAdmin,
    adminReady,
    reminderCount,
    checkOnLogin,
    fetchAllReminders,
    fetchReminderCount,
    removeReminder,
    resetSession
  };
};